import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../lib/api';
import { useAuthStore } from '../../hooks/useAuth';
import toast from 'react-hot-toast';
import { X, Package, Plus, Search, Truck, Loader2, CheckCircle, ChevronDown } from 'lucide-react';

interface CreateLoadDialogProps {
    open: boolean;
    onClose: () => void;
    onCreated: () => void;
    initialPoIds?: string[];
}

export function CreateLoadDialog({ open, onClose, onCreated, initialPoIds }: CreateLoadDialogProps) {
    const { user } = useAuthStore();
    const [creating, setCreating] = useState(false);
    const [poSearch, setPoSearch] = useState('');
    const [pos, setPos] = useState<any[]>([]);
    const [poLoading, setPoLoading] = useState(false);
    const [selectedPOs, setSelectedPOs] = useState<any[]>([]);

    const [form, setForm] = useState({
        type: 'inbound',
        carrierName: '',
        carrierMcNumber: '',
        carrierEmail: '',
        carrierPhone: '',
        driverName: '',
        truckNumber: '',
        trailerNumber: '',
        trailerType: '',
        eta: '',
        notes: '',
    });
    const [carrierOpen, setCarrierOpen] = useState(false);

    // Load POs for the combobox
    const searchPOs = useCallback(async (q: string) => {
        setPoLoading(true);
        try {
            const res = await api.searchWarehousePOs(q, '');
            setPos(res.results || []);
        } catch (err) {
            console.error('PO search error:', err);
        } finally {
            setPoLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            searchPOs('');
            // If initial POs, load them
            if (initialPoIds && initialPoIds.length > 0) {
                // We'll add them once POs load
            }
        }
    }, [open, searchPOs]);

    // When POs load and we have initialPoIds, select them
    useEffect(() => {
        if (initialPoIds && initialPoIds.length > 0 && pos.length > 0 && selectedPOs.length === 0) {
            const matched = pos.filter(p => initialPoIds.includes(p.id));
            if (matched.length > 0) {
                // Derive direction from first PO, keep only same-direction POs
                const direction = (matched[0].type || 'inbound').toLowerCase();
                const sameDirection = matched.filter(p => (p.type || 'inbound').toLowerCase() === direction);
                setForm(f => ({ ...f, type: direction }));
                setSelectedPOs(sameDirection.map(p => ({
                    id: p.id,
                    poNumber: p.customerPoNumber,
                    clientName: p.customerName || p.clientName || 'Unknown',
                    pallets: p.totalSkids || 0,
                    weight: p.totalWeight || 0,
                    type: (p.type || 'inbound').toLowerCase(),
                })));
            }
        }
    }, [pos, initialPoIds]);

    const handleSelectPO = (poId: string) => {
        const po = pos.find(p => p.id === poId);
        if (!po || selectedPOs.some(s => s.id === poId)) return;
        // Lock direction to first PO's type
        const poDirection = (po.type || 'inbound').toLowerCase();
        if (selectedPOs.length === 0) {
            setForm(f => ({ ...f, type: poDirection }));
        }
        setSelectedPOs(prev => [...prev, {
            id: po.id,
            poNumber: po.customerPoNumber,
            clientName: po.customerName || po.clientName || 'Unknown',
            pallets: po.totalSkids || 0,
            weight: po.totalWeight || 0,
            type: poDirection,
        }]);
    };

    const handleRemovePO = (poId: string) => {
        setSelectedPOs(prev => {
            const next = prev.filter(p => p.id !== poId);
            return next;
        });
    };

    const totalPallets = selectedPOs.reduce((s, p) => s + p.pallets, 0);
    const totalWeight = selectedPOs.reduce((s, p) => s + p.weight, 0);

    const handleSubmit = async () => {
        setCreating(true);
        try {
            const result = await api.createLoad({
                ...form,
                trailerType: form.trailerType || undefined,
                eta: form.eta || undefined,
                poIds: selectedPOs.map(p => p.id),
            });
            toast.success(`Load ${result.loadNumber} created`, { icon: '≡뿯ƽ뿯½뿯½', duration: 3000 });
            onCreated();
            onClose();
            // Reset form
            setSelectedPOs([]);
            setForm({
                type: 'inbound', carrierName: '', carrierMcNumber: '', carrierEmail: '',
                carrierPhone: '', driverName: '', truckNumber: '', trailerNumber: '',
                trailerType: '', eta: '', notes: '',
            });
        } catch (err: any) {
            const msg = err.message || 'Failed to create load';
            toast.error(msg);
        } finally {
            setCreating(false);
        }
    };

    if (!open) return null;

    const directionLocked = selectedPOs.length > 0;
    const availablePOs = pos.filter(p => {
        if (selectedPOs.some(s => s.id === p.id)) return false;
        if (p.activeLoadId) return false;
        // Filter by current load direction
        const poDir = (p.type || 'inbound').toLowerCase();
        if (poDir !== form.type) return false;
        return true;
    });
    const takenPOs = pos.filter(p => !selectedPOs.some(s => s.id === p.id) && p.activeLoadId);

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-slate-200 dark:border-slate-800 sticky top-0 bg-white dark:bg-slate-900 z-10">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                            <Truck className="text-cyan-500" size={22} />
                            Create New Load
                        </h2>
                        <p className="text-sm text-slate-500 mt-0.5">Use a load when one truck is carrying multiple POs.</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">If this shipment is just one PO, you can schedule it without creating a load first.</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700 dark:hover:text-white transition-colors">
                        <X size={22} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Type selector */}
                    <div className="flex gap-2 items-center">
                        {['inbound', 'outbound'].map(t => (
                            <button
                                key={t}
                                type="button"
                                disabled={directionLocked}
                                onClick={() => { if (!directionLocked) setForm(f => ({ ...f, type: t })); }}
                                className={`px-4 py-2 rounded-lg text-sm font-bold transition-all border ${form.type === t
                                    ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-400'
                                    : 'bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                                    } ${directionLocked ? 'cursor-not-allowed opacity-60' : ''}`}
                            >
                                {t === 'inbound' ? '≡뿯ƽ뿯½뿯½ Inbound' : '≡뿯ƽ뿯½뿯½ Outbound'}
                            </button>
                        ))}
                        {directionLocked && (
                            <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-1">Locked to {form.type} 뿯ν뿯½뿯½ remove POs to change</span>
                        )}
                    </div>

                    {/* PO Selection 뿯ν뿯½뿯½ PROMOTED TO TOP */}
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                <Package size={14} className="text-cyan-400" />
                                Link Purchase Orders
                            </h3>
                            <span className="text-[10px] font-bold text-slate-800 dark:text-slate-200 bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded-full">Required</span>
                        </div>
                        <div className="relative mb-3">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                            <input
                                type="text"
                                value={poSearch}
                                onChange={(e) => { setPoSearch(e.target.value); searchPOs(e.target.value); }}
                                placeholder="Search PO number..."
                                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                            />
                        </div>
                        {directionLocked && (
                            <p className="text-[11px] text-amber-500 dark:text-amber-400 font-medium mb-2 -mt-1">Only {form.type} POs can be added to this load.</p>
                        )}

                        {/* Available POs dropdown */}
                        {availablePOs.length > 0 && (
                            <div className="border border-slate-200 dark:border-slate-700 rounded-lg max-h-40 overflow-y-auto mb-3">
                                {availablePOs.slice(0, 10).map(po => (
                                    <button
                                        key={po.id}
                                        type="button"
                                        onClick={() => handleSelectPO(po.id)}
                                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-cyan-500/10 text-left text-sm transition-colors border-b border-slate-100 dark:border-slate-800 last:border-0"
                                    >
                                        <div>
                                            <span className="font-mono font-bold text-slate-900 dark:text-white">{po.customerPoNumber}</span>
                                            <span className="text-slate-400 ml-2 text-xs">{po.customerName || po.clientName}</span>
                                        </div>
                                        <span className="text-xs text-slate-500">{po.totalSkids || 0} skids</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Taken POs 뿯ν뿯½뿯½ disabled, with reason */}
                        {takenPOs.length > 0 && (
                            <div className="border border-slate-200 dark:border-slate-700 border-t-0 rounded-b-lg max-h-24 overflow-y-auto">
                                {takenPOs.slice(0, 5).map(po => (
                                    <div
                                        key={po.id}
                                        className="w-full flex items-center justify-between px-3 py-2 text-left text-sm border-b border-slate-100 dark:border-slate-800 last:border-0 opacity-50 cursor-not-allowed"
                                    >
                                        <div>
                                            <span className="font-mono font-bold text-slate-500 dark:text-slate-500">{po.customerPoNumber}</span>
                                            <span className="text-slate-400 ml-2 text-xs">{po.customerName || po.clientName}</span>
                                        </div>
                                        <span className="text-[10px] font-bold text-amber-500 dark:text-amber-400">Linked to {po.activeLoadNumber}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Selected POs */}
                        {selectedPOs.length > 0 && (
                            <div className="space-y-1.5">
                                <div className="text-xs text-slate-500 font-medium">Selected ({selectedPOs.length})</div>
                                {selectedPOs.map(po => (
                                    <div key={po.id} className="flex items-center justify-between px-3 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <Package size={14} className="text-cyan-400" />
                                            <span className="text-sm font-mono font-bold text-slate-900 dark:text-white">{po.poNumber}</span>
                                            <span className="text-xs text-slate-400">뿯ν뿯½뿯½ {po.clientName}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-slate-500">{po.pallets} pallets</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemovePO(po.id)}
                                                className="text-slate-400 hover:text-red-400 transition-colors"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <div className="flex gap-4 text-xs text-slate-500 mt-2 px-1">
                                    <span>Total: <strong className="text-slate-900 dark:text-white">{totalPallets}</strong> pallets</span>
                                    <span>Weight: <strong className="text-slate-900 dark:text-white">{totalWeight.toLocaleString()}</strong> lbs</span>
                                </div>
                                <p className="text-xs font-medium text-cyan-600 dark:text-cyan-400 mt-1.5 px-1">{selectedPOs.length} PO{selectedPOs.length !== 1 ? 's' : ''} linked to this load</p>
                            </div>
                        )}
                    </div>

                    {/* Carrier & Transport 뿯ν뿯½뿯½ collapsible */}
                    <div>
                        <button
                            type="button"
                            onClick={() => setCarrierOpen(o => !o)}
                            className="flex items-center gap-2 w-full text-left mb-3"
                        >
                            <Truck size={14} className="text-indigo-400" />
                            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex-1">Carrier & Transport</h3>
                            <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full mr-1">Optional</span>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${carrierOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {carrierOpen && (
                            <>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { name: 'carrierName', label: 'Carrier Name', placeholder: 'ABC Trucking' },
                                { name: 'carrierMcNumber', label: 'MC Number', placeholder: '123456' },
                                { name: 'driverName', label: 'Driver Name', placeholder: 'John Smith' },
                                { name: 'truckNumber', label: 'Truck #', placeholder: 'TRK-001' },
                                { name: 'trailerNumber', label: 'Trailer #', placeholder: 'TRL-001' },
                            ].map(field => (
                                <div key={field.name} className="space-y-1">
                                    <label className="text-xs text-slate-500">{field.label}</label>
                                    <input
                                        type="text"
                                        value={(form as any)[field.name]}
                                        onChange={(e) => setForm(f => ({ ...f, [field.name]: e.target.value }))}
                                        placeholder={field.placeholder}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:border-cyan-500 outline-none"
                                    />
                                </div>
                            ))}
                            <div className="space-y-1">
                                <label className="text-xs text-slate-500">Trailer Type</label>
                                <select
                                    value={form.trailerType}
                                    onChange={(e) => setForm(f => ({ ...f, trailerType: e.target.value }))}
                                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:border-cyan-500 outline-none appearance-none"
                                >
                                    <option value="">Select...</option>
                                    <option value="Van">Dry Van</option>
                                    <option value="Reefer">Reefer</option>
                                    <option value="Flatbed">Flatbed</option>
                                </select>
                            </div>
                        </div>

                    {/* ETA + Notes */}
                    <div className="grid grid-cols-2 gap-3 mt-3">
                        <div className="space-y-1">
                            <label className="text-xs text-slate-500">ETA</label>
                            <input
                                type="datetime-local"
                                value={form.eta}
                                onChange={(e) => setForm(f => ({ ...f, eta: e.target.value }))}
                                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:border-cyan-500 outline-none [color-scheme:dark]"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs text-slate-500">Notes</label>
                            <input
                                type="text"
                                value={form.notes}
                                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                                placeholder="Special instructions..."
                                className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:border-cyan-500 outline-none"
                            />
                        </div>
                    </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 p-6 border-t border-slate-200 dark:border-slate-800 sticky bottom-0 bg-white dark:bg-slate-900">
                    <span className="text-xs text-slate-400 dark:text-slate-500">You can edit this later</span>
                    <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={creating || selectedPOs.length === 0}
                        className="px-6 py-2.5 rounded-lg text-sm font-bold bg-cyan-600 hover:bg-cyan-500 text-white transition-all disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-cyan-500/20"
                    >
                        {creating ? (
                            <><Loader2 size={16} className="animate-spin" /> Creating...</>
                        ) : (
                            <><CheckCircle size={16} /> Create Load</>
                        )}
                    </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

